import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal, WritableSignal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription, debounceTime, distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DepartmentService } from 'src/app/services/api/departments/department.service';
import { DisbursementCertificationService } from 'src/app/services/api/disbursement/disbursement.service';
import { DisbursementRoundService } from 'src/app/services/api/disbursement/disbursement-round.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { DepartmentModel } from 'src/app/services/models/department.model';
import {
  DISBURSEMENT_COMPLETED_STATUS_CODES,
  DisbursementCertificationModel,
  DisbursementRosterRow,
  DisbursementStatusCode,
} from 'src/app/services/models/disbursement/disbursement.model';
import {
  DisbursementRoundBreakdown,
  DisbursementRoundBreakdownItem,
  DisbursementRoundModel,
  DisbursementRoundStatusCode,
} from 'src/app/services/models/disbursement/disbursement-round.model';
import {
  DisbursementStatusAudience,
  DisbursementStatusDisplay,
  disbursementStatusAudienceFor,
  getDisbursementStatusDisplay,
} from 'src/app/services/models/disbursement/disbursement-status-display';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { StatusTone } from 'src/shared/status-badge/status-badge.component';
import { SharedModule } from 'src/shared/shared.module';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  currentAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';
import { extractBlobErrorMessage } from 'src/utils/blob-error';
import {
  DISBURSEMENT_ROUND_EXPORT_ROLE_IDS,
  DISBURSEMENT_SELF_CERTIFY_ROLE_IDS,
  RoleID,
} from 'src/utils/enum/role.enum';

const EXPORT_PREVIEW_ERROR_FALLBACK = 'ไม่สามารถโหลดตัวอย่างเอกสารรวมของรอบนี้ได้';
const EXPORT_ERROR_FALLBACK = 'ไม่สามารถดาวน์โหลดเอกสารรวมของรอบนี้ได้';

// SIT sprint 2 — the roster now speaks the same short vocabulary the อาจารย์ page uses
// (SUMMARY_OVERRIDE_BY_STATUS): which station holds a row is answered by the
// "ผู้ที่ต้องดำเนินการ" column instead of by the status chip.
const ROSTER_ROUND_BADGE: Record<DisbursementRoundStatusCode, DisbursementStatusDisplay> = {
  draft: { tone: 'pending', label: 'รอดำเนินการ' },
  pending_finance_sign: { tone: 'pending', label: 'รอดำเนินการ' },
  pending_head: { tone: 'pending', label: 'รอดำเนินการ' },
  pending_vice_dean_admin: { tone: 'pending', label: 'รอดำเนินการ' },
  completed: { tone: 'completed', label: 'เสร็จสิ้น' },
  returned_to_officer: { tone: 'revision', label: 'ส่งกลับแก้ไข' },
  cancelled: { tone: 'special_revision', label: 'ยกเลิก' },
};

// Same idea as the certification page's NEXT_ACTOR_BY_STATUS, resolved from the roster's
// phase vocabulary instead of approval_track (roster rows carry no track — many of them have
// no certification at all yet).
const ROSTER_ROUND_ACTOR: Record<DisbursementRoundStatusCode, string> = {
  draft: 'เจ้าหน้าที่สารบรรณ',
  pending_finance_sign: 'เจ้าหน้าที่การเงิน',
  pending_head: 'หัวหน้าสาขา',
  pending_vice_dean_admin: 'รองคณบดีฝ่ายบริหาร',
  completed: '-',
  returned_to_officer: 'เจ้าหน้าที่สารบรรณ',
  cancelled: '-',
};

type RegistryTab = 'list' | 'rounds';

interface RegistryStatusOption {
  value: string;
  label: string;
}

// Status sub-tabs inside the "list" panel, matching the disbursement-certification
// page's CertificationTab exactly (same 4 keys/labels) — isRoundExportRole only
// (admin's plain list + เจ้าหน้าที่สารบรรณ's roster); every other role keeps the
// สถานะ dropdown instead (see showStatusFilter).
type RegistryStatusTab = 'all' | 'pending' | 'completed' | 'revision';

interface RegistryStatusTabOption {
  key: RegistryStatusTab;
  label: string;
}

const REGISTRY_STATUS_TABS: readonly RegistryStatusTabOption[] = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'pending', label: 'รอดำเนินการ' },
  { key: 'completed', label: 'เสร็จสิ้น' },
  { key: 'revision', label: 'ส่งกลับแก้ไข' },
];

interface RegistryRow {
  id: number;
  teacherName: string;
  departmentId: number | null;
  departmentName: string;
  academicTerm: string;
  academicYear: string;
  updatedAtLabel: string;
  statusCode: DisbursementStatusCode | null;
  statusLabel: string;
  pendingActor: string;
  certificationId: number | null;
  inRound: boolean;
  badgeTone: StatusTone;
  badgeLabel: string;
  badgeTooltip: string | null;
  raw: DisbursementCertificationModel | null;
}

interface RegistryDataSource {
  data: RegistryRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-disbursement-registry',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './registry.component.html',
  styleUrl: './registry.component.scss',
})
export class DisbursementRegistryComponent implements OnInit, OnDestroy {
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<unknown>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<unknown>;

  filterForm = inject(FormBuilder).group({
    academicTermId: [null as number | null],
    academicTerm: [null as string | null],
    academicYear: [null as string | null],
    departmentId: [null as number | null],
    statusFilter: [null as string | null],
    keywords: [''],
  });

  // Its own group: the round list is loaded once per term and filtered in memory, so this
  // control must never re-run the paginated registry query the toolbar above drives.
  roundFilterForm = inject(FormBuilder).group({
    documentNumber: [''],
  });

  columns: unknown[] = [];
  datasource = signal<RegistryDataSource>(this.createDataSource([]));
  tableLoading = signal(false);

  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];
  departmentOptions: DepartmentModel[] = [];
  statusOptions: RegistryStatusOption[] = [];

  activeTab = signal<RegistryTab>('list');
  statusTab = signal<RegistryStatusTab>('all');
  readonly statusTabs = REGISTRY_STATUS_TABS;

  // v2.0 — every completed รอบส่งเบิก the reader's round scope allows, each exported as
  // ONE merged PDF. This page is the module's only download surface: per-document
  // downloads stay closed until the round completes, so Export is where a สารบรรณ
  // finally gets the paper.
  completedRounds = signal<DisbursementRoundModel[]>([]);
  completedRoundsLoading = signal(false);
  roundDocumentNumberFilter = signal('');
  // The round whose export modal is open — null closes it.
  exportTarget = signal<DisbursementRoundModel | null>(null);
  exportPreviewLoading = signal(false);
  exportPreviewError = signal<string | null>(null);
  exportPreviewUrl = signal<SafeResourceUrl | null>(null);
  exportDownloading = signal(false);
  // แจกแจงยอดรายอาจารย์ของรอบ กางจาก dropdown ข้างปุ่ม Export — โหลดตอนกางครั้งแรกแล้ว cache
  // ไว้ทั้งหน้า: รอบที่ completed แล้ว snapshot ของใบรับรองถูก freeze ไปแล้ว ยอดจึงเปลี่ยนไม่ได้
  expandedBreakdownIds = signal<ReadonlySet<number>>(new Set<number>());
  breakdownLoadingIds = signal<ReadonlySet<number>>(new Set<number>());
  breakdownErrorIds = signal<ReadonlySet<number>>(new Set<number>());
  private roundBreakdowns = signal<Record<number, DisbursementRoundBreakdown>>({});

  // Kept raw so it can be revoked — the sanitized signal above cannot be turned back
  // into the blob: URL createObjectURL handed out.
  private rawExportPreviewUrl: string | null = null;
  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private academicTerms: AcademicTermYearModel[] = [];
  private lastRoundsTermId: number | null = null;
  private readonly subscriptions = new Subscription();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    status: 'status',
    updatedAtLabel: 'updated_at',
  };
  private readonly authService = inject(AuthService);
  private readonly departmentService = inject(DepartmentService);
  private readonly disbursementService = inject(DisbursementCertificationService);
  private readonly roundService = inject(DisbursementRoundService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  ngOnInit(): void {
    if (this.isDocumentOfficerRole) {
      this.initRosterColumns();
    } else {
      this.initColumns();
      this.statusOptions = this.buildCertificationStatusOptions();
    }

    this.listenFilters();
    this.loadDepartmentOptions();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokeExportPreview();
  }

  get isDocumentOfficerRole(): boolean {
    return this.authService.getActiveRoleId() === RoleID.DOCUMENT_OFFICER;
  }

  // DISBURSEMENT_ROUND_EXPORT_ROLE_IDS (admin + document_officer), split out 2026-08-26 — context/decisions/DECISION_RolesPermissions.md
  get isRoundExportRole(): boolean {
    const roleId = this.authService.getActiveRoleId();
    return roleId != null && DISBURSEMENT_ROUND_EXPORT_ROLE_IDS.includes(roleId);
  }

  get isTeacherRole(): boolean {
    return this.authService.getActiveRoleId() === RoleID.TEACHER;
  }

  get isSelfCertifyRole(): boolean {
    const roleId = this.authService.getActiveRoleId();
    return roleId != null && DISBURSEMENT_SELF_CERTIFY_ROLE_IDS.includes(roleId);
  }

  // อาจารย์ only ever sees their own certifications, so a สาขาวิชา picker, a keyword box and
  // a status picker are all filters over a single-owner list — dropped for that role only.
  get showDepartmentFilter(): boolean {
    return !this.isTeacherRole;
  }

  get showKeywordSearch(): boolean {
    return !this.isTeacherRole && this.activeTab() === 'list';
  }

  readonly keywordSearchLabel = 'ชื่อ - นามสกุล';

  // isRoundExportRole (admin + เจ้าหน้าที่สารบรรณ) now filters status through
  // showStatusSubTabs instead — same split the certification page draws between its
  // status dropdown-less roles and its CertificationTab roles.
  get showStatusFilter(): boolean {
    return !this.isTeacherRole && !this.isRoundExportRole;
  }

  get showStatusSubTabs(): boolean {
    return this.isRoundExportRole && this.activeTab() === 'list';
  }

  // A getter, not a cached field: switching role must re-render the badges.
  get statusAudience(): DisbursementStatusAudience {
    return disbursementStatusAudienceFor(this.authService.getActiveRoleId());
  }

  onSearch(): void {
    this.loadRegistry(1);
  }

  onTabChange(tab: RegistryTab): void {
    this.activeTab.set(tab);
  }

  onStatusTabChange(tab: RegistryStatusTab): void {
    if (this.statusTab() === tab) {
      return;
    }

    this.statusTab.set(tab);
    this.loadRegistry(1);
  }

  onView(row: RegistryRow): void {
    if (row.certificationId == null) {
      return;
    }

    void this.router.navigate(['/disbursement-management/registry', row.certificationId]);
  }

  // --- v2.0 per-round Export (สารบรรณ + admin) ---

  // The card headline is the เลขที่ — what a สารบรรณ files and searches by; สาขา and
  // วันที่ ride the meta line below it. The modal keeps completedRoundLabel, which still
  // has to disambiguate ภาค + รอบที่ next to the same เลขที่.
  completedRoundTitle(round: DisbursementRoundModel): string {
    const documentNumber = this.documentNumberOf(round);
    return documentNumber !== '' ? `เลขที่ ${documentNumber}` : 'ยังไม่ระบุเลขที่เอกสาร';
  }

  // The whole completed-round set for the term is already in memory (one unpaginated
  // request), so the เลขที่ search filters it here instead of re-querying.
  filteredCompletedRounds(): DisbursementRoundModel[] {
    const keyword = this.roundDocumentNumberFilter().trim().toLowerCase();
    if (keyword === '') {
      return this.completedRounds();
    }

    return this.completedRounds().filter(
      (round) => this.documentNumberOf(round).toLowerCase().includes(keyword),
    );
  }

  completedRoundDepartment(round: DisbursementRoundModel): string {
    return round.department?.name_th ?? round.department?.name ?? round.department?.name_en ?? '-';
  }

  completedRoundLabel(round: DisbursementRoundModel): string {
    return `${this.completedRoundDepartment(round)} — ${round.path_label} รอบที่ ${round.round_no ?? '-'}`;
  }

  // Every number here is already on the round row the list fetched — never a second request.
  exportModalSubtitle(round: DisbursementRoundModel): string {
    const documentNumber = this.documentNumberOf(round);

    return [
      `เลขที่ ${documentNumber === '' ? '-' : documentNumber}`,
      `ลงวันที่ ${this.formatDate(round.issued_on)}`,
      `${round.member_count} รายการ`,
      `รวม ${this.formatAmount(round.total_amount)} บาท`,
    ].join(' · ');
  }

  formatAmount(value: number | null | undefined): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }

  // Export opens a preview first: the merged PDF is the whole ส่งเบิก bundle, and the
  // officer is the last person who can catch a wrong round before it leaves the system.
  onOpenExportModal(round: DisbursementRoundModel): void {
    if (!round.export_ready) {
      return;
    }

    this.revokeExportPreview();
    this.exportTarget.set(round);
    this.exportPreviewError.set(null);
    this.exportPreviewLoading.set(true);

    this.roundService.downloadMerged(round.id).pipe(take(1)).subscribe({
      next: (blob) => {
        // The user may have closed the modal (or opened another round) while this was
        // in flight — drop the blob instead of leaking an object URL nobody revokes.
        if (this.exportTarget()?.id !== round.id) {
          return;
        }

        this.rawExportPreviewUrl = window.URL.createObjectURL(blob);
        this.exportPreviewUrl.set(
          this.sanitizer.bypassSecurityTrustResourceUrl(`${this.rawExportPreviewUrl}#toolbar=0&navpanes=0`),
        );
        this.exportPreviewLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        void extractBlobErrorMessage(err, EXPORT_PREVIEW_ERROR_FALLBACK).then((message) => {
          if (this.exportTarget()?.id !== round.id) {
            return;
          }

          this.exportPreviewError.set(message);
          this.exportPreviewLoading.set(false);
        });
      },
    });
  }

  // เบิกรายวิชา: ใบรับรอง 1 ใบ = 1 วิชา/section อาจารย์คนเดียวจึงกระจายหลายใบ — backend
  // รวมยอดข้ามใบมาให้แล้ว ที่นี่แค่ตัดสินใจว่าจะมีปุ่มกางไหม (ภาคปกติ 1 ใบ = 1 คน ไม่ต้องแจกแจง)
  hasBreakdownToggle(round: DisbursementRoundModel): boolean {
    return round.path === 'international';
  }

  isBreakdownExpanded(round: DisbursementRoundModel): boolean {
    return this.expandedBreakdownIds().has(round.id);
  }

  isBreakdownLoading(round: DisbursementRoundModel): boolean {
    return this.breakdownLoadingIds().has(round.id);
  }

  hasBreakdownError(round: DisbursementRoundModel): boolean {
    return this.breakdownErrorIds().has(round.id);
  }

  breakdownOf(round: DisbursementRoundModel): DisbursementRoundBreakdown | null {
    return this.roundBreakdowns()[round.id] ?? null;
  }

  breakdownListId(round: DisbursementRoundModel): string {
    return `disbRegBreakdown-${round.id}`;
  }

  breakdownToggleTitle(round: DisbursementRoundModel): string {
    return this.isBreakdownExpanded(round)
      ? 'ซ่อนรายละเอียดการเบิกจ่ายรายบุคคล'
      : 'ดูรายละเอียดการเบิกจ่ายรายบุคคล';
  }

  // กางครั้งแรก = ยิงคำขอ, ครั้งถัดไปใช้ค่าที่ cache ไว้ — ยกเว้นครั้งที่ล้มเหลว (ไม่ cache ความล้มเหลว)
  // ผู้ใช้จึงกดปิด-เปิดใหม่เพื่อลองอีกครั้งได้
  onToggleBreakdown(round: DisbursementRoundModel): void {
    const next = new Set(this.expandedBreakdownIds());
    if (!next.delete(round.id)) {
      next.add(round.id);
    }

    this.expandedBreakdownIds.set(next);
    if (next.has(round.id) && this.roundBreakdowns()[round.id] == null) {
      this.loadBreakdown(round);
    }
  }

  breakdownItemLabel(item: DisbursementRoundBreakdownItem): string {
    const subject = [item.subject_code, item.subject_name].filter((part) => (part ?? '') !== '').join(' ');
    const section = (item.section ?? '') === '' ? '' : ` (Sec. ${item.section})`;

    return subject === '' ? `รายวิชา${section}` : `${subject}${section}`;
  }

  onCloseExportModal(): void {
    if (this.exportDownloading()) {
      return;
    }

    this.closeExportModal();
  }

  onConfirmExport(): void {
    const round = this.exportTarget();
    if (round == null || this.exportDownloading()) {
      return;
    }

    this.exportDownloading.set(true);
    this.roundService.exportRound(round.id).pipe(
      take(1),
      finalize(() => this.exportDownloading.set(false)),
    ).subscribe({
      // closeExportModal, not onCloseExportModal — finalize has not cleared the
      // downloading flag yet at this point, and that flag is the close button's guard.
      next: (blob) => {
        this.downloadBlob(blob, this.exportFileName(round));
        this.closeExportModal();
      },
      error: (err) => {
        console.error(err);
        // 422 while the post-signature re-render is still running — the modal stays open
        // so the officer can retry without hunting for the round again.
        void extractBlobErrorMessage(err, EXPORT_ERROR_FALLBACK).then((message) => this.toast.toastError(message));
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadRegistry(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadRegistry(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadRegistry(1);
  }

  formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  private ownerLabel(cert: DisbursementCertificationModel): string {
    if (cert.user != null) {
      return cert.user.full_name;
    }

    if (cert.subject != null) {
      return `${cert.subject.code} ${cert.subject.name}`.trim();
    }

    return '-';
  }

  // Declared rather than left to the shared datatable's injected "#" column — same prop,
  // so normalizeColumns() reuses this one and the row numbering still fills in.
  private orderColumn(): unknown {
    return {
      prop: 'order',
      name: 'ลำดับ',
      sortable: false,
      draggable: false,
      resizeable: false,
      width: 72,
      minWidth: 72,
      maxWidth: 72,
      cellClass: 'text-center',
      headerClass: 'd-flex justify-content-center',
    };
  }

  private initColumns(): void {
    this.columns = [
      this.orderColumn(),
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: false,
        width: 190,
        minWidth: 170,
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: false,
        width: 170,
        minWidth: 150,
      },
      // อาจารย์ reads one term at a time through the toolbar filters, so both columns
      // repeat what the filters already say — dropped for that role only.
      ...(this.isTeacherRole ? [] : [
        {
          prop: 'academicTerm',
          name: 'เทอมการศึกษา',
          sortable: false,
          width: 110,
          minWidth: 100,
        },
        {
          prop: 'academicYear',
          name: 'ปีการศึกษา',
          sortable: false,
          width: 115,
          minWidth: 105,
        },
      ]),
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: true,
        cellTemplate: this.statusTemplate,
        width: 180,
        minWidth: 170,
      },
      {
        prop: 'updatedAtLabel',
        name: 'อัปเดตล่าสุด',
        sortable: true,
        width: 140,
        minWidth: 130,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center disb-reg-action-cell',
        headerClass: 'text-center disb-reg-action-header',
      },
    ];
  }

  private initRosterColumns(): void {
    this.columns = [
      this.orderColumn(),
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: false,
        width: 240,
        minWidth: 200,
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: false,
        width: 170,
        minWidth: 150,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: false,
        cellTemplate: this.statusTemplate,
        width: 260,
        minWidth: 220,
      },
      {
        prop: 'pendingActor',
        name: 'ผู้ที่ต้องดำเนินการ',
        sortable: false,
        width: 170,
        minWidth: 150,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center disb-reg-action-cell',
        headerClass: 'text-center disb-reg-action-header',
      },
    ];
  }

  // Only called for showStatusFilter roles now — admin (isRoundExportRole) filters
  // through showStatusSubTabs instead, so the scope=completed slice this reads under
  // is always the whole option set: pool + finished disclose no station either way.
  private buildCertificationStatusOptions(): RegistryStatusOption[] {
    return DISBURSEMENT_COMPLETED_STATUS_CODES.map((code) => ({
      value: code,
      label: getDisbursementStatusDisplay(code, this.statusAudience).label,
    }));
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.valueChanges
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe(() => this.loadRegistry(1)),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicTerm.valueChanges.subscribe(() => this.syncAcademicTermId()),
    );
    this.subscriptions.add(
      this.roundFilterForm.controls.documentNumber.valueChanges
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value) => this.roundDocumentNumberFilter.set(String(value ?? ''))),
    );
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.filterForm.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.filterForm.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.filterForm.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.filterForm.patchValue({ academicTermId: resolved?.id ?? null }, { emitEvent: false });
  }

  private loadDepartmentOptions(): void {
    this.departmentService.getDepartments({ limit: 200 }).pipe(take(1)).subscribe({
      next: (response) => {
        this.departmentOptions = response.data;
      },
      error: (err) => console.error(err),
    });
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTermYears().pipe(take(1)).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        const defaultTerm = currentAcademicTerm(terms, this.authService.currentUser()?.academic_term);
        const defaultYear = defaultTerm != null ? String(toBuddhistYear(defaultTerm.year)) : null;
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, defaultYear);
        this.filterForm.patchValue(
          {
            academicYear: defaultYear,
            academicTerm: defaultTerm != null ? String(defaultTerm.name).trim() : null,
            academicTermId: defaultTerm?.id ?? null,
          },
          { emitEvent: false },
        );
        this.loadRegistry(1);
      },
      error: (err) => {
        console.error(err);
        this.loadRegistry(1);
      },
    });
  }

  private loadRegistry(page: number): void {
    if (this.isDocumentOfficerRole) {
      this.loadRosterRegistry(page);
    } else {
      this.loadCompletedRegistry(page);
    }

    this.loadCompletedRoundsBlock();
  }

  private loadCompletedRoundsBlock(): void {
    if (!this.isRoundExportRole) {
      return;
    }

    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.lastRoundsTermId = null;
      this.completedRounds.set([]);
      return;
    }

    if (academicTermId === this.lastRoundsTermId) {
      return;
    }

    this.lastRoundsTermId = academicTermId;
    this.resetBreakdowns();
    this.completedRoundsLoading.set(true);
    this.roundService.getRounds({ academic_term_id: academicTermId, status: 'completed', limit: 50 }).pipe(
      finalize(() => this.completedRoundsLoading.set(false)),
    ).subscribe({
      next: (response) => this.completedRounds.set(response.data),
      error: (err) => {
        console.error(err);
        this.lastRoundsTermId = null;
        this.completedRounds.set([]);
      },
    });
  }

  private resetBreakdowns(): void {
    this.expandedBreakdownIds.set(new Set<number>());
    this.breakdownLoadingIds.set(new Set<number>());
    this.breakdownErrorIds.set(new Set<number>());
    this.roundBreakdowns.set({});
  }

  private loadCompletedRegistry(page: number): void {
    this.currentPage = page;
    this.tableLoading.set(true);
    this.disbursementService.getCertifications({
      ...this.buildFilterParams(),
      // Registry search is เลขที่เอกสาร/ชื่อคน — the department axis has its own filter here.
      keyword_scope: 'no_department' as const,
      ...this.certificationScopeParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort as 'status' | 'updated_at' | null,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.datasource.set(this.createDataSource(
          response.data.map((cert) => this.mapCertificationToRow(cert)),
          response.page,
          response.limit,
          response.total,
          response.total_pages,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private loadRosterRegistry(page: number): void {
    this.currentPage = page;
    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.datasource.set(this.createDataSource([], page, this.currentLimit));
      return;
    }

    this.tableLoading.set(true);
    this.disbursementService.getRoster({ academic_term_id: academicTermId }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        const rows = response.rows.map((row, index) => this.mapRosterRowToRow(row, index));
        this.datasource.set(this.paginateRosterRows(rows, page));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  // เจ้าหน้าที่สารบรรณ is always isRoundExportRole, so the roster never shows the สถานะ
  // dropdown — it filters through statusTab instead. rosterBadgeFor already speaks the
  // same 3 words the tabs use ('รอดำเนินการ'/'เสร็จสิ้น'/'ส่งกลับแก้ไข'), so matching on
  // the resolved badgeLabel needs no separate status-code mapping.
  private rosterStatusFilterValue(): string | null {
    switch (this.statusTab()) {
      case 'pending':
        return 'รอดำเนินการ';
      case 'completed':
        return 'เสร็จสิ้น';
      case 'revision':
        return 'ส่งกลับแก้ไข';
      case 'all':
      default:
        return null;
    }
  }

  private paginateRosterRows(rows: RegistryRow[], page: number): RegistryDataSource {
    const { departmentId, keywords } = this.filterForm.getRawValue();
    const normalizedKeywords = String(keywords ?? '').trim().toLowerCase();
    const statusFilterValue = this.rosterStatusFilterValue();
    const filtered = rows.filter((row) => {
      if (departmentId != null && row.departmentId !== departmentId) {
        return false;
      }

      if (statusFilterValue != null && row.badgeLabel !== statusFilterValue) {
        return false;
      }

      // WS-A leftover: สาขาวิชา has its own filter here, so matching the keyword against the
      // department name only widened every search into a whole-department match.
      return normalizedKeywords === '' || row.teacherName.toLowerCase().includes(normalizedKeywords);
    });

    const total = filtered.length;
    const totalPage = Math.max(Math.ceil(total / this.currentLimit), 1);
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);
    const start = (normalizedPage - 1) * this.currentLimit;

    return this.createDataSource(
      filtered.slice(start, start + this.currentLimit),
      normalizedPage,
      this.currentLimit,
      total,
      totalPage,
    );
  }

  private buildFilterParams(): {
    academic_term_id?: number;
    department_id?: number;
    status?: DisbursementStatusCode;
    keywords?: string;
  } {
    const { academicTermId, departmentId, statusFilter, keywords } = this.filterForm.getRawValue();
    const params: {
      academic_term_id?: number;
      department_id?: number;
      status?: DisbursementStatusCode;
      keywords?: string;
    } = {};

    if (academicTermId != null) {
      params.academic_term_id = academicTermId;
    }

    if (departmentId != null) {
      params.department_id = departmentId;
    }

    if (statusFilter != null && statusFilter !== '') {
      params.status = statusFilter as DisbursementStatusCode;
    }

    const normalizedKeywords = String(keywords ?? '').trim();
    if (normalizedKeywords !== '') {
      params.keywords = normalizedKeywords;
    }

    return params;
  }

  // Non-roster roles filter here. isRoundExportRole means admin on this path (เจ้าหน้าที่
  // สารบรรณ never reaches loadCompletedRegistry) — it now drives the same status-tab
  // buckets the certification page's applyStatusTabFilter uses, replacing the old
  // unconditional "admin sees every station, everyone else scope:'completed'" split.
  // Every other role still reads only the finished/pool slice, unaffected by statusTab.
  private certificationScopeParams(): {
    scope?: 'completed' | 'pending';
    status?: DisbursementStatusCode;
    ownership?: 'mine';
  } {
    if (!this.isRoundExportRole) {
      return {
        scope: 'completed',
        ...(this.isSelfCertifyRole ? { ownership: 'mine' as const } : {}),
      };
    }

    switch (this.statusTab()) {
      case 'completed':
        return { status: 'completed' };
      case 'revision':
        return { status: 'revision_schedule_requested' };
      case 'pending':
        return { scope: 'pending' };
      case 'all':
      default:
        return {};
    }
  }

  private mapCertificationToRow(cert: DisbursementCertificationModel): RegistryRow {
    const badge = getDisbursementStatusDisplay(cert.status_code, this.statusAudience);

    return {
      id: cert.id,
      teacherName: this.ownerLabel(cert),
      departmentId: cert.department.id,
      departmentName: cert.department.name_th ?? cert.department.name ?? cert.department.name_en ?? '-',
      academicTerm: String(cert.academic_term.name),
      academicYear: String(toBuddhistYear(cert.academic_term.year)),
      updatedAtLabel: this.formatDate(cert.updated_at),
      statusCode: cert.status_code,
      statusLabel: badge.label,
      pendingActor: '-',
      certificationId: cert.id,
      inRound: false,
      badgeTone: badge.tone,
      badgeLabel: badge.label,
      badgeTooltip: null,
      raw: cert,
    };
  }

  private mapRosterRowToRow(row: DisbursementRosterRow, index: number): RegistryRow {
    const badge = this.rosterBadgeFor(row);

    return {
      id: row.certification_id ?? -(index + 1),
      teacherName: this.rosterOwnerLabel(row),
      departmentId: row.department.id,
      departmentName: row.department.name_th ?? row.department.name ?? row.department.name_en ?? '-',
      academicTerm: '',
      academicYear: '',
      updatedAtLabel: '-',
      statusCode: row.status_code,
      statusLabel: badge.label,
      pendingActor: this.rosterActorFor(row),
      certificationId: row.certification_id,
      inRound: row.in_round,
      badgeTone: badge.tone,
      badgeLabel: badge.label,
      badgeTooltip: this.rosterReasonFor(row),
      raw: null,
    };
  }

  // The chip label stays a coarse bucket (see rosterBadgeFor below) so this page and
  // admin's never word a status differently — but a row that is stuck deserves to say
  // why. readiness_missing carries the same reasons ProvisionService skipped on, and the
  // API now fills it for `missed` rows too, which is what "ไม่ผ่านเกณฑ์" without any
  // explanation had left the tester guessing about.
  private rosterReasonFor(row: DisbursementRosterRow): string | null {
    const reasons = (row.readiness_missing ?? [])
      .map((reason) => String(reason ?? '').trim())
      .filter((reason) => reason !== '');

    return reasons.length > 0 ? reasons.join('\n') : null;
  }

  // Roster-local on purpose: the shared getDisbursementRosterPhaseDisplay still serves the
  // cover-memos hub's own table, which keeps the granular station vocabulary.
  //
  // Tester feedback (2026-08-24): readiness used to print its own reason text
  // (missing ERP/ตารางสอน feed, below-minimum workload, ...) instead of this
  // bucket's label — now every readiness reason collapses to รอดำเนินการ, same
  // as admin's list, so the two pages never disagree on wording. The specific
  // reason is still available upstream (Disbursement::ProvisionService), this
  // page just no longer surfaces it in the chip.
  rosterBadgeFor(row: DisbursementRosterRow): DisbursementStatusDisplay {
    switch (row.phase) {
      case 'readiness':
      case 'waiting_teacher':
      case 'waiting_finance':
      case 'ready_for_round':
        return { tone: 'pending', label: 'รอดำเนินการ' };
      case 'in_revision':
        return { tone: 'revision', label: 'ส่งกลับแก้ไข' };
      case 'disqualified':
        return getDisbursementStatusDisplay(row.status_code, 'summary');
      case 'in_round':
        return row.round_status_code != null
          ? { ...ROSTER_ROUND_BADGE[row.round_status_code] }
          : { tone: 'pending', label: 'รอดำเนินการ' };
      case 'completed':
        return { tone: 'completed', label: 'เสร็จสิ้น' };
      case 'missed':
        return { tone: 'inert', label: 'ไม่ผ่านเกณฑ์' };
      case 'expired':
        return { tone: 'special_revision', label: 'ไม่สำเร็จ (หมดเขต)' };
    }
  }

  rosterActorFor(row: DisbursementRosterRow): string {
    switch (row.phase) {
      case 'waiting_teacher':
        return 'อาจารย์';
      case 'waiting_finance':
        return 'เจ้าหน้าที่การเงิน';
      case 'in_revision':
        return 'รองคณบดีฝ่ายวิชาการ';
      case 'ready_for_round':
        return 'เจ้าหน้าที่สารบรรณ';
      case 'in_round':
        return row.round_status_code != null ? ROSTER_ROUND_ACTOR[row.round_status_code] : '-';
      case 'readiness':
      case 'disqualified':
      case 'completed':
      case 'missed':
      case 'expired':
        return '-';
    }
  }

  private rosterOwnerLabel(row: DisbursementRosterRow): string {
    if (row.kind === 'course') {
      const subject = row.subject;
      const base = subject != null ? `${subject.code} ${subject.name}`.trim() : '-';
      return row.section != null && row.section !== '' ? `${base} (กลุ่ม ${row.section})` : base;
    }

    return row.user?.full_name ?? '-';
  }

  private createDataSource(
    rows: RegistryRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
  ): RegistryDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private closeExportModal(): void {
    this.revokeExportPreview();
    this.exportTarget.set(null);
    this.exportPreviewError.set(null);
    this.exportPreviewLoading.set(false);
  }

  // อ่านจาก compensation_snapshot ที่ freeze ไว้ (endpoint แยกจาก round_json โดยตั้งใจ) —
  // ล้มเหลวต้องบอกในกล่องที่กางออกมา ไม่ใช่เงียบ: ผู้ใช้กดเองจึงรอผลอยู่
  private loadBreakdown(round: DisbursementRoundModel): void {
    this.setBreakdownFlag(this.breakdownErrorIds, round.id, false);
    this.setBreakdownFlag(this.breakdownLoadingIds, round.id, true);

    this.roundService.getBreakdown(round.id).pipe(
      take(1),
      finalize(() => this.setBreakdownFlag(this.breakdownLoadingIds, round.id, false)),
    ).subscribe({
      next: (breakdown) => this.roundBreakdowns.update((current) => ({ ...current, [round.id]: breakdown })),
      error: (err) => {
        console.error(err);
        this.setBreakdownFlag(this.breakdownErrorIds, round.id, true);
      },
    });
  }

  private setBreakdownFlag(target: WritableSignal<ReadonlySet<number>>, roundId: number, on: boolean): void {
    const next = new Set(target());
    if (on) {
      next.add(roundId);
    } else {
      next.delete(roundId);
    }

    target.set(next);
  }

  private revokeExportPreview(): void {
    if (this.rawExportPreviewUrl != null) {
      window.URL.revokeObjectURL(this.rawExportPreviewUrl);
      this.rawExportPreviewUrl = null;
    }

    this.exportPreviewUrl.set(null);
  }

  // The blob download cannot read the backend's Content-Disposition, so the Thai file
  // name is rebuilt here from the round's เลขที่.
  private exportFileName(round: DisbursementRoundModel): string {
    const documentNumber = this.documentNumberOf(round);

    return documentNumber === ''
      ? `disbursement_round_${round.id}.pdf`
      : `รอบส่งเบิก-${documentNumber}.pdf`;
  }

  private documentNumberOf(round: DisbursementRoundModel): string {
    return String(round.document_number ?? '').trim();
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
